import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

export type NavTab = {
  id: string;      // matches section id, e.g. "notes"
  label: string;   // display label, e.g. "Notes"
};

type Props = {
  tabs: NavTab[];
};

/**
 * Sticky horizontal sub-navigation bar for ChapterPage.
 *
 * - Tabs are pill buttons that smooth-scroll to their matching section.
 * - The active tab is highlighted via IntersectionObserver scrollspy.
 * - On mobile the pill row is horizontally scrollable (no wrapping).
 * - The bar sticks at top:0 (inside the <article> scroll context so it
 *   naturally respects the AppShell header above).
 */
export function ChapterNav({ tabs }: Props) {
  const [activeId, setActiveId] = useState<string>(tabs[0]?.id ?? '');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tabs.length === 0) return;

    const observers: IntersectionObserver[] = [];

    // Track which sections are currently visible.
    const visibleRatios = new Map<string, number>();

    tabs.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;

      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry) {
            visibleRatios.set(id, entry.intersectionRatio);
            // Pick the section with the highest intersection ratio.
            let best = '';
            let bestRatio = -1;
            visibleRatios.forEach((ratio, sectionId) => {
              if (ratio > bestRatio) {
                bestRatio = ratio;
                best = sectionId;
              }
            });
            if (best) setActiveId(best);
          }
        },
        // Use a tall rootMargin so a section activates as soon as any part
        // enters the middle viewport band.
        { threshold: [0, 0.1, 0.25, 0.5, 1.0], rootMargin: '-10% 0px -50% 0px' },
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, [tabs]);

  // Scroll the active pill into view within the nav bar on mobile.
  useEffect(() => {
    if (!scrollRef.current) return;
    const pill = scrollRef.current.querySelector<HTMLButtonElement>(
      `[data-tab="${activeId}"]`,
    );
    if (!pill) return;
    // Scroll the pill row horizontally only. scrollIntoView() would also scroll
    // the WINDOW vertically when the nav is off-screen (e.g. on first load on a
    // phone), yanking the reader away from the top of the chapter.
    const row = scrollRef.current;
    const rowRect = row.getBoundingClientRect();
    const pillRect = pill.getBoundingClientRect();
    row.scrollTo({
      left: row.scrollLeft + (pillRect.left - rowRect.left) - (rowRect.width - pillRect.width) / 2,
      behavior: 'smooth',
    });
  }, [activeId]);

  const handleClick = (id: string) => {
    if (!document.getElementById(id)) return;
    setActiveId(id);
    // Shareable deep-link to the section, without pushing a history entry.
    if (history.replaceState) history.replaceState(null, '', `#${id}`);

    const offset = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--ch-scroll-offset'),
      10,
    ) || 112;
    // Compute the target fresh each time and jump (instant — window.scrollTo
    // works everywhere, unlike iOS Safari's scrollIntoView({behavior:'smooth'})).
    const jump = () => {
      const el = document.getElementById(id);
      if (!el) return;
      // Force an INSTANT scroll, overriding the global `scroll-behavior: smooth`.
      // Otherwise window.scrollTo animates smoothly and the 100ms re-pin keeps
      // interrupting its own scroll → the page barely moves ("small jump, stays
      // in the same area"). Override inline, jump, restore.
      const root = document.documentElement;
      const prev = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, el.getBoundingClientRect().top + window.scrollY - offset);
      root.style.scrollBehavior = prev;
    };
    // The page below grows as lazy images load, so a single jump lands short of
    // the section. Jump now, then re-pin every 100ms until the target stops
    // moving (layout settled) — capped at ~1.5s. This reliably lands ON the
    // section without jittering once things settle.
    jump();
    let stable = 0;
    let attempts = 0;
    let lastTop = NaN;
    const timer = window.setInterval(() => {
      const el = document.getElementById(id);
      if (!el) return window.clearInterval(timer);
      const top = Math.round(el.getBoundingClientRect().top + window.scrollY - offset);
      window.scrollTo(0, top);
      stable = top === lastTop ? stable + 1 : 0;
      lastTop = top;
      if (stable >= 2 || ++attempts >= 15) window.clearInterval(timer);
    }, 100);
  };

  return (
    <nav
      aria-label="Chapter sections"
      className="aces-scroll-progress sticky top-14 z-20 bg-cream/85 backdrop-blur-xl backdrop-saturate-150 border-b border-ink-200/70 -mx-4 px-4 shadow-nav"
    >
      <div
        ref={scrollRef}
        className="flex gap-1.5 overflow-x-auto py-2.5 scrollbar-hide"
        style={{ scrollbarWidth: 'none' }}
      >
        {tabs.map(({ id, label }) => {
          const isActive = activeId === id;
          return (
            <button
              key={id}
              data-tab={id}
              onClick={() => handleClick(id)}
              className={clsx(
                'flex-shrink-0 px-4 py-1.5 rounded-full border border-transparent text-sm font-semibold transition-[color,background-color,border-color,box-shadow] duration-200',
                'focus-visible:ring-2 focus-visible:ring-offset-1',
                !isActive &&
                  'text-ink-600 hover:text-ink-900 hover:bg-ink-100/80 hover:border-ink-200',
              )}
              style={
                isActive
                  ? {
                      backgroundColor: 'var(--subject-color)',
                      color: '#fff',
                      // subtle shadow tinted to subject colour via currentColor trick
                      boxShadow: '0 1px 2px color-mix(in srgb, var(--subject-color) 30%, transparent), 0 6px 14px -6px color-mix(in srgb, var(--subject-color) 55%, transparent)',
                    }
                  : undefined
              }
              aria-current={isActive ? 'true' : undefined}
            >
              {label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
